import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { connect } from 'react-redux'
import { Field, change, untouch } from 'redux-form'
import { get, trim } from 'lodash'
import {
  Text,
  InputFieldReduxFormWrapper,
  SelectFieldReduxFormWrapper,
  MaskedInputFieldReduxFormWrapper,
  DatePickerFieldReduxFormWrapper,
  CheckboxFieldReduxFormWrapper,
  constants as tkConstants,
  utils as tkUtils,
} from '@adc/ui-core'
import InternationalCheckbox from '../internationalCheckbox'
import { over18 } from '../../../utils/forms'
import { formatTitlelize } from '../../../../../util/formatters'
import { stateOptions } from '../../../entityForm/shared/internal'
import { maritalStatusOptions } from '../utils'
import { MULTI_CO_BUYER_FORM_NAME } from '../constants'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const ELMID = 'co_buyer_form'
const { required, format } = tkUtils.formValidators

class CoBuyerForm extends Component {
  static propTypes = {
    index: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired,
    fields: PropTypes.array,
    formKey: PropTypes.string,
    isAutoCapitalize: PropTypes.bool,
  }

  clearStateCityZipFields = value => {
    const { formKey, dispatch } = this.props
    if (value) {
      dispatch(change(MULTI_CO_BUYER_FORM_NAME, `${formKey}.city`, ''))
      dispatch(change(MULTI_CO_BUYER_FORM_NAME, `${formKey}.state`, ''))
      dispatch(change(MULTI_CO_BUYER_FORM_NAME, `${formKey}.zipCode`, ''))
      dispatch(untouch(MULTI_CO_BUYER_FORM_NAME, `${formKey}.zipCode`))
      dispatch(untouch(MULTI_CO_BUYER_FORM_NAME, `${formKey}.state`))
      dispatch(untouch(MULTI_CO_BUYER_FORM_NAME, `${formKey}.city`))
    }
  }

  onMaritalStatusChange = (_, value) => {
    const { index, fields, dispatch } = this.props
    const currentCobuyer = fields[index]
    if (value !== 'MARRIED_MAN' && value !== 'MARRIED_WOMAN') {
      dispatch(
        change(
          MULTI_CO_BUYER_FORM_NAME,
          `relationship_${currentCobuyer.buyerId}`,
          ''
        )
      )
    }
  }

  render() {
    const {
      fields,
      formKey,
      index = 0,
      dispatch,
      isAutoCapitalize,
    } = this.props
    const noMiddleName = Boolean(get(fields[index], 'noMiddleName'))
    const isInternationalChecked = Boolean(
      get(fields[index], 'internationalCheckbox')
    )

    const maritalStatusSelectOptions = maritalStatusOptions(
      `${formKey}.${ELMID}_marital_status`
    )

    const normalizeFunc = isAutoCapitalize ? formatTitlelize : trim

    return (
      <div className={cx('root', 'ui', 'form')}>
        <div className={cx('field-wrapper')}>
          <Field
            className={cx('field')}
            name={`${formKey}.firstName`}
            component={InputFieldReduxFormWrapper}
            label='Legal First Name'
            elmId={`${formKey}.${ELMID}_first_name`}
            validate={required()}
            normalizeOnBlur={normalizeFunc}
            type='text'
          />
        </div>
        <div className={cx('field-wrapper')}>
          <Field
            name={`${formKey}.middleName`}
            component={InputFieldReduxFormWrapper}
            label='Legal Full Middle Name'
            elmId={`${formKey}.${ELMID}_middle_name`}
            disabled={noMiddleName}
            validate={!noMiddleName ? required() : null}
            normalizeOnBlur={normalizeFunc}
            type='text'
          />

          <Field
            name={`${formKey}.noMiddleName`}
            component={CheckboxFieldReduxFormWrapper}
            onChange={(_, value) =>
              value &&
              dispatch(
                change(MULTI_CO_BUYER_FORM_NAME, `${formKey}.middleName`, '')
              )
            } // eslint-disable-line
            checkboxLabel='I confirm that I do not have a middle name.'
            elmId={`${formKey}.${ELMID}_no_middle_name`}
          />
          <Text.Body size='small' color='gray' className={cx('u-pl-4')}>
            As this contract is a binding legal document, your legal middle name
            is required. Contracts that do not have accurate information may be
            rejected.
          </Text.Body>
        </div>
        <div className={cx('field-wrapper')}>
          <Field
            className={cx('field')}
            name={`${formKey}.lastName`}
            component={InputFieldReduxFormWrapper}
            label='Legal Last Name'
            elmId={`${formKey}.${ELMID}_last_name`}
            validate={required()}
            normalizeOnBlur={normalizeFunc}
            type='text'
          />
          <Text.Body size='small' color='gray'>
            Full legal name is needed for identification to gain access to the
            contract and for closing deed preparation.
          </Text.Body>
        </div>
        <div className={cx('field-wrapper')}>
          <Field
            className={cx('field')}
            name={`${formKey}.streetName`}
            component={InputFieldReduxFormWrapper}
            label='Address'
            elmId={`${formKey}.${ELMID}_street_name`}
            validate={required()}
            normalizeOnBlur={normalizeFunc}
            type='text'
          />
          <InternationalCheckbox
            name={`${formKey}.internationalCheckbox`}
            elmId={`${formKey}.${ELMID}`}
            onChange={(_, value) => this.clearStateCityZipFields(value)}
            isInternationalChecked={isInternationalChecked}
          />
        </div>
        {!isInternationalChecked && (
          <>
            <div className={cx('field-wrapper')}>
              <Field
                className={cx('field')}
                name={`${formKey}.city`}
                component={InputFieldReduxFormWrapper}
                label='City'
                elmId={`${formKey}.${ELMID}_city`}
                validate={required()}
                normalizeOnBlur={trim}
                type='text'
              />
            </div>
            <div className={cx('field-wrapper')}>
              <Field
                className={cx('field')}
                name={`${formKey}.state`}
                component={SelectFieldReduxFormWrapper}
                label='State'
                elmId={`${formKey}.${ELMID}_state`}
                options={stateOptions(`${formKey}.${ELMID}`)}
                validate={required()}
                placeholder='Select'
              />
            </div>
            <div className={cx('field-wrapper')}>
              <Field
                className={cx('field')}
                name={`${formKey}.zipCode`}
                component={MaskedInputFieldReduxFormWrapper}
                label='Zip Code'
                elmId={`${formKey}.${ELMID}_zip_code`}
                validate={[
                  required(),
                  format({
                    with: tkConstants.regex.ZIPCODE_SYNTAX,
                    msg: 'Invalid Zipcode',
                  }),
                ]}
                mask={tkUtils.inputMasks.zipcodeMask}
                type='text'
              />
            </div>
          </>
        )}
        <div className={cx('field-wrapper')}>
          <Field
            className={cx('field')}
            name={`${formKey}.phone`}
            component={MaskedInputFieldReduxFormWrapper}
            label='Phone Number'
            elmId={`${formKey}.${ELMID}_phone`}
            validate={required()}
            mask={tkUtils.inputMasks.phoneNumberMask}
            normalize={value => value.replace(/[ ()-]/g, '')}
            type='text'
          />
        </div>
        <div className={cx('field-wrapper')}>
          <Field
            className={cx('field')}
            name={`${formKey}.email`}
            component={InputFieldReduxFormWrapper}
            label='E-mail Address'
            elmId={`${formKey}.${ELMID}_email`}
            validate={[
              required(),
              format({
                with: tkConstants.regex.EMAIL_SYNTAX,
                msg: 'Invalid email',
              }),
            ]}
            type='text'
          />
          <Text.Body size='small' color='gray'>
            Important communications regarding your purchase will be sent via
            email. Please make sure to check your email often (including spam
            folder) and add the Auction.com domain to your approved list.
          </Text.Body>
        </div>
        <div className={cx('field-wrapper')}>
          <Field
            className={cx('field')}
            name={`${formKey}.dob`}
            component={DatePickerFieldReduxFormWrapper}
            modifiable
            label='Date of Birth'
            elmId={`${formKey}.${ELMID}_dob`}
            validate={[required(), over18()]}
          />
        </div>
        <div className={cx('field-wrapper')}>
          <Field
            className={cx('field')}
            name={`${formKey}.maritalStatus`}
            component={SelectFieldReduxFormWrapper}
            label='Marital Status'
            elmId={`${formKey}.${ELMID}_marital_status`}
            validate={required()}
            placeholder='Select'
            onChange={this.onMaritalStatusChange}
            upward
            subtitle='This information is required by the Seller and used in populating the contract.'
            options={maritalStatusSelectOptions}
          />
          <Text.Body size='small' color='gray'>
            This information is required by the Seller and used in populating
            the contract.
          </Text.Body>
        </div>
      </div>
    )
  }
}
// Just for storybook test
export default connect((state, props) => {
  return {
    fields: get(state, `form.${MULTI_CO_BUYER_FORM_NAME}.values.cobuyers`),
  }
})(CoBuyerForm)
